import React, { FC, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TextEncoder } from 'text-encoding-shim';

import { IState } from '../../reducers';
import { IConfigReducer } from '../../reducers/configReducer';
import { IContextReducer } from '../../reducers/contextReducer';
import { IAnalyticsReducer } from '../../reducers/analyticsReducer';
import { ETrackingType } from '../../entities/IAnalytics';
import { addTrackingScript, isTrackingScriptAdded } from '../../tools/analyticsTools';
import { setTracking } from '../../actions/analyticsActions';
import { selectStonlyEnabled } from '../../tools/platformConfiguration';
import { isStonlyScriptAdded, setStonlyWidgetVisibility, shouldLoadStonlyWidget, STONLY_WIDGET_SCRIPT_ATTR } from '../../tools/stonlyWidget';

export const Tracking: FC<React.PropsWithChildren<unknown>> = () => {
    const dispatch = useDispatch();
    const { appConfig, isLoggedIn, pageContext, isShadowAuth, isInExportView, stonlyEnabled, platformConfigurationStatus } = useSelector<IState, IConfigReducer & IContextReducer & IAnalyticsReducer & { stonlyEnabled: boolean }>(state => ({
        ...state.config,
        ...state.context,
        ...state.analytics,
        stonlyEnabled: selectStonlyEnabled(state)
    }));

    const digestMessage = async (message: string) => {
        const msgUint8 = new TextEncoder().encode(message);
        const crypto = window.crypto || window.msCrypto;
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const lcTrackingData = appConfig?.smh;
        const userEmail = pageContext?.user?.email;

        if (!window.location.host.includes('local') && !isShadowAuth && !isInExportView && !params.get('export')) {
            if (!isTrackingScriptAdded() && isLoggedIn) {
                const initCallback = async () => {
                    try {

                        const hashedUserEmail = await digestMessage(userEmail);

                        const Matomo = window.Matomo || window.Piwik;

                        const lcTracker = lcTrackingData?.trackingUrl && Matomo?.getTracker?.(`${lcTrackingData.trackingUrl}matomo.php`, lcTrackingData.trackingId);

                        [{
                            tracker: lcTracker,
                            trackingType: ETrackingType.Smh
                        }].forEach(trackerObject => {
                            trackerObject.tracker?.setUserId?.(hashedUserEmail);
                            trackerObject.tracker?.setCustomUrl?.(window.location.href);
                            trackerObject.tracker?.trackPageView?.();
                            trackerObject.tracker && dispatch(setTracking(trackerObject.trackingType, trackerObject.tracker));
                        });
                    } catch (error) {
                        console.error(error);
                    }
                };

                window.matomoAsyncInit = initCallback;
                window.piwikAsyncInit = initCallback;

                const lcScript = lcTrackingData?.trackingUrl && `${lcTrackingData?.trackingUrl}matomo.js`;
                lcScript && addTrackingScript(lcScript);
            }
        }

    }, [isLoggedIn]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const canLoadStonly = shouldLoadStonlyWidget({
            stonlyEnabled,
            platformConfigurationLoaded: platformConfigurationStatus === 'loaded',
            isLocalHost: window.location.host.includes('local'),
            isShadowAuth,
            isInExportView,
            hasExportQuery: !!params.get('export')
        });

        if (!canLoadStonly) {
            setStonlyWidgetVisibility(false);
            return;
        }

        if (isStonlyScriptAdded()) {
            setStonlyWidgetVisibility(true);
            return;
        }

        const script = document.createElement('script');
        script.src = require('!!url-loader!../../tools/stonlyScript.js').default;
        script.async = true;
        script.setAttribute(STONLY_WIDGET_SCRIPT_ATTR, 'true');
        document.body.appendChild(script);
    }, [stonlyEnabled, platformConfigurationStatus, isShadowAuth, isInExportView]);

    return <></>;
};

export default Tracking;
